import { useEffect, useState } from "react";

export default function HeroSection() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <section id="hero" className="relative min-h-screen flex flex-col" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      {/* Background */}
      <div className="absolute inset-0 bg-[#080808]">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20dark%20technology%20background%20with%20glowing%20green%20neon%20grid%20lines%2C%20financial%20data%20visualization%2C%20minimal%20futuristic%20aesthetic%2C%20dark%20black%20background%20with%20subtle%20emerald%20light%20patterns%2C%20high%20contrast%2C%20digital%20art&width=1920&height=1080&seq=hero-bg-01&orientation=landscape"
          alt="background"
          className="w-full h-full object-cover object-center opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/90" />
      </div>

      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-black/95 shadow-lg" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[#00FF88] text-2xl font-black tracking-tight">SUPER<span className="text-white">PACK</span></span>
          </div>
          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {["sistemas", "bonos", "testimonios", "faq"].map((section) => (
              <button
                key={section}
                onClick={() => scrollTo(section)}
                className="text-gray-400 hover:text-white transition-colors text-sm font-medium capitalize cursor-pointer whitespace-nowrap"
              >
                {section === "faq" ? "FAQ" : section.charAt(0).toUpperCase() + section.slice(1)}
              </button>
            ))}
            <button
              onClick={() => scrollTo("hero-cta")}
              className="bg-[#00FF88] text-black font-bold text-sm px-5 py-2 rounded-full hover:bg-[#00e07a] transition-all cursor-pointer whitespace-nowrap"
            >
              Acceso Inmediato
            </button>
          </div>
          {/* Mobile hamburger */}
          <button
            className="md:hidden flex items-center justify-center w-9 h-9 text-white cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`ri-${menuOpen ? "close" : "menu"}-line text-2xl`} />
          </button>
        </div>
        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-black/98 px-6 pb-6 flex flex-col gap-4">
            {["sistemas", "bonos", "testimonios", "faq"].map((section) => (
              <button
                key={section}
                onClick={() => scrollTo(section)}
                className="text-gray-300 hover:text-white text-left text-sm font-medium capitalize cursor-pointer py-2 border-b border-white/10"
              >
                {section === "faq" ? "FAQ" : section.charAt(0).toUpperCase() + section.slice(1)}
              </button>
            ))}
            <button
              onClick={() => scrollTo("hero-cta")}
              className="bg-[#00FF88] text-black font-bold text-sm px-5 py-3 rounded-full hover:bg-[#00e07a] transition-all cursor-pointer whitespace-nowrap text-center mt-2"
            >
              Acceso Inmediato
            </button>
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-24 sm:py-28 lg:py-32 w-full grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left content */}
          <div className="flex flex-col gap-5 sm:gap-6">
            {/* Pre-titular */}
            <div className="inline-flex items-start sm:items-center gap-2">
              <span className="inline-block bg-[#FF6B35]/15 border border-[#FF6B35]/40 text-[#FF6B35] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full leading-snug">
                ⚠️ Atención Estrategas: Deja de perder dinero por el desorden.
              </span>
            </div>

            {/* Main headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-[1.08] text-white">
              EL{" "}
              <span className="text-[#00FF88]" style={{ textShadow: "0 0 30px rgba(0,255,136,0.4)" }}>
                SUPER PACK
              </span>{" "}
              DEFINITIVO PARA{" "}
              <span className="text-[#00FF88]" style={{ textShadow: "0 0 30px rgba(0,255,136,0.4)" }}>
                GESTIONAR TUS FINANZAS
              </span>{" "}
              Y ESCALAR SIN FRICCIÓN.
            </h1>

            {/* Subtitle */}
            <p className="text-gray-400 text-base sm:text-lg leading-relaxed max-w-xl" style={{ fontFamily: "'Inter', sans-serif" }}>
              Dos programas top:{" "}
              <strong className="text-white">Stock Simple + Budgettrack</strong>. Tu inventario y tus números
              controlados desde tu mano por menos de lo que cuesta una cena.
            </p>

            {/* CTA 1 */}
            <div id="hero-cta" className="flex flex-col sm:flex-row gap-4 mt-2">
              <a
                href="#bonos"
                onClick={(e) => { e.preventDefault(); scrollTo("bonos"); }}
                className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-[#FF6B35] to-[#FF3D00] text-white font-black text-base md:text-lg px-8 py-5 rounded-2xl hover:scale-105 transition-all duration-300 cursor-pointer text-center shadow-lg shadow-orange-900/40 w-full sm:w-auto"
                style={{ animation: "pulse-glow 2.5s infinite" }}
              >
                <span className="text-xl flex-shrink-0">👉</span>
                <span className="leading-snug">
                  SÍ, QUIERO EL CONTROL TOTAL<br className="block" />
                  POR S/ 25 AL AÑO
                </span>
              </a>
            </div>

            {/* Trust badges */}
            <div className="flex items-center gap-6 mt-2 flex-wrap">
              {[
                { icon: "ri-shield-check-line", text: "100% Seguro" },
                { icon: "ri-cloud-line", text: "En la Nube" },
                { icon: "ri-smartphone-line", text: "Cualquier Dispositivo" },
              ].map((badge) => (
                <div key={badge.text} className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${badge.icon} text-[#00FF88] text-base`} />
                  </div>
                  <span className="text-gray-400 text-xs font-medium">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right visual */}
          <div className="hidden lg:flex items-center justify-center">
            <div className="relative w-full max-w-lg">
              <div className="absolute -inset-4 bg-[#00FF88]/10 rounded-3xl blur-2xl" />
              <img
                src="https://readdy.ai/api/search-image?query=professional%20financial%20dashboard%20on%20laptop%20and%20smartphone%20screens%20floating%2C%20dark%20background%2C%20green%20neon%20data%20charts%20and%20graphs%2C%20inventory%20management%20interface%20with%20clean%20minimal%20UI%20design%2C%20high%20tech%20product%20mockup%2C%20glowing%20emerald%20accents%2C%20dark%20moody%20atmosphere&width=600&height=500&seq=hero-mockup-02&orientation=landscape"
                alt="Dashboard de Stock Simple y Budgettrack"
                className="relative rounded-2xl w-full h-auto object-cover"
                style={{ boxShadow: "0 0 60px rgba(0,255,136,0.15), 0 20px 60px rgba(0,0,0,0.8)" }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="relative z-10 pb-10 flex justify-center">
        <div className="flex flex-col items-center gap-2 animate-bounce">
          <span className="text-gray-500 text-xs tracking-widest uppercase">Descubre más</span>
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-arrow-down-line text-[#00FF88]" />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(255,107,53,0.4), 0 8px 30px rgba(255,61,0,0.3); }
          50% { box-shadow: 0 0 40px rgba(255,107,53,0.7), 0 8px 40px rgba(255,61,0,0.5); }
        }
      `}</style>
    </section>
  );
}
